import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material';
import { useState } from 'react';
import CheckIcon from '@mui/icons-material/Check';
import { useTranslation } from '../../hooks/useTranslation';
import { Button } from '../../components/Button';
import api from '../../services/api';

const ApprovalSection = ({ id, accessStage, onActionComplete }) => {
  const { t } = useTranslation();

  const [approving, setApproving] = useState(false);
  const [rejectModalOpen, setRejectModalOpen] = useState(false);
  const [rejecting, setRejecting] = useState(false);
  const [resetting, setResetting] = useState(false);

  const handleReject = async () => {
    try {
      setRejecting(true);
      await api.patch(`/v1/resale/${id}/reject`);
      onActionComplete?.();
    } catch (error) {
      console.error(t('messages.rejectError'), error);
    } finally {
      setRejecting(false);
      setRejectModalOpen(false);
    }
  };

  const handleApprove = async () => {
    try {
      setApproving(true);
      await api.patch(`/v1/resale/${id}/approve`);
      onActionComplete?.();
    } catch (error) {
      console.error(t('messages.rejectError'), error);
    } finally {
      setApproving(false);
    }
  };

  const handleResetAccess = async () => {
    try {
      setResetting(true);
      await api.patch(`/v1/resale/${id}/reset-access`);
      onActionComplete?.();
    } catch (error) {
      console.error(t('messages.approveError'), error);
    } finally {
      setResetting(false);
    }
  };

  return (
    <>
      <Box
        sx={{
          border: '1px solid #e0e0e0',
          borderTop: `3px solid ${accessStage === 'REJECTED' ? '#d32f2f' : '#83d84d'}`,
          borderRadius: '8px',
          px: 3,
          py: 2,
          mb: 2,
          backgroundColor: '#fff',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 2,
        }}
      >
        <Box>
          <Typography sx={{ fontWeight: 700, fontSize: '14px', color: '#1F2937' }}>
            {t('resale.approvalSection.title')}
          </Typography>
          <Typography sx={{ fontSize: '13px', color: '#757575', mt: '2px' }}>
            {t('resale.approvalSection.description')}
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', gap: 1, flexShrink: 0 }}>
          {accessStage === 'REJECTED' ? (
            <Button
              variant="outlined"
              fullWidth={false}
              loading={resetting}
              onClick={handleResetAccess}
              sx={{
                py: '6px',
                px: '16px',
                color: '#1565c0',
                border: '1px solid #1565c0',
                '&:hover': { backgroundColor: '#e3f2fd', borderColor: '#1565c0' },
              }}
            >
              {t('buttons.allowNewRequest')}
            </Button>
          ) : (
            <>
              <Button
                variant="outlined"
                fullWidth={false}
                onClick={() => setRejectModalOpen(true)}
                sx={{
                  py: '6px',
                  px: '16px',
                  color: '#d32f2f',
                  border: '1px solid #d32f2f',
                  '&:hover': { backgroundColor: '#fff5f5', borderColor: '#d32f2f' },
                }}
              >
                {t('buttons.reject')}
              </Button>

              <Button
                variant="contained"
                fullWidth={false}
                startIcon={<CheckIcon />}
                loading={approving}
                onClick={handleApprove}
                sx={{ py: '6px', px: '16px' }}
              >
                {t('buttons.approveClient')}
              </Button>
            </>
          )}
        </Box>
      </Box>

      <Dialog open={rejectModalOpen} onClose={() => setRejectModalOpen(false)}>
        <DialogTitle>{t('resale.rejectModal.title')}</DialogTitle>
        <DialogContent>
          <Typography>{t('resale.rejectModal.confirmText')}</Typography>
        </DialogContent>
        <DialogActions>
          <Button
            variant="text"
            fullWidth={false}
            onClick={() => setRejectModalOpen(false)}
            disabled={rejecting}
          >
            {t('common.cancel')}
          </Button>
          <Button
            variant="outlined"
            fullWidth={false}
            loading={rejecting}
            onClick={handleReject}
            sx={{
              py: '6px',
              px: '16px',
              color: '#d32f2f',
              border: '1px solid #d32f2f',
              '&:hover': { backgroundColor: '#fff5f5', borderColor: '#d32f2f' },
            }}
          >
            {t('buttons.reject')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ApprovalSection;
